import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { ReactNode } from 'react'
import { BAND_ORDER, bandMeta } from '../lib/bands'
import type { TrendPoint, bandDonut, stackedByArea } from '../features/dashboard/models'
import { BandShapeIcon, cx } from './ui'

const AXIS = { fontSize: 12, fill: 'var(--ink-faint)' }

/** Hatch pattern for "not assessed" so it is never just a grey fill. */
export function HatchDefs() {
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true" focusable="false">
      <defs>
        <pattern id="hatch-na" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="6" height="6" fill="var(--band-na-soft)" />
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--band-na)" strokeWidth="2" />
        </pattern>
      </defs>
    </svg>
  )
}

function bandFill(band: string): string {
  return band === 'not_assessed' ? 'url(#hatch-na)' : bandMeta(band).color
}

/* ---------- stacked bars: equipment per band per area ---------- */

type Row = ReturnType<typeof stackedByArea>[number]

export function StackedAreaBars({ rows, mode, bandFilter, onSelectArea, caption = 'Equipos por banda en cada área', groupLabel = 'Área' }: { rows: Row[]; mode: 'count' | 'share'; bandFilter: string; onSelectArea?: (area: number) => void; caption?: string; groupLabel?: string }) {
  const bands = BAND_ORDER.filter((b) => bandFilter === 'all' || b === bandFilter)
  const data = rows.map((r) => {
    const total = BAND_ORDER.reduce((sum, b) => sum + r[b], 0)
    const out: Record<string, number | string> = { name: r.code, full: r.name, area: r.area, total }
    for (const b of BAND_ORDER) out[b] = mode === 'share' ? (total ? Math.round((1000 * r[b]) / total) / 10 : 0) : r[b]
    return out
  })
  return (
    <div className="chart" style={{ height: 280 }}>
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 520, height: 280 }}>
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -8 }} barCategoryGap="22%">
          <CartesianGrid vertical={false} stroke="var(--line)" />
          <XAxis dataKey="name" tick={AXIS} tickLine={false} axisLine={false} />
          <YAxis tick={AXIS} tickLine={false} axisLine={false} unit={mode === 'share' ? '%' : ''} allowDecimals={false} />
          <Tooltip cursor={{ fill: 'var(--brand-soft)', opacity: 0.4 }} content={<StackTooltip mode={mode} />} />
          {bands.map((b, i) => (
            <Bar key={b} dataKey={b} stackId="a" fill={bandFill(b)} radius={i === bands.length - 1 ? [8, 8, 0, 0] : 0} isAnimationActive={false} onClick={(d) => onSelectArea?.(Number((d as unknown as { area: number }).area))} cursor={onSelectArea ? 'pointer' : undefined} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <table className="sr-only">
        <caption>{caption}</caption>
        <thead><tr><th>{groupLabel}</th>{BAND_ORDER.map((b) => <th key={b}>{bandMeta(b).label}</th>)}</tr></thead>
        <tbody>{rows.map((r) => <tr key={r.code}><th>{r.name}</th>{BAND_ORDER.map((b) => <td key={b}>{r[b]}</td>)}</tr>)}</tbody>
      </table>
    </div>
  )
}

function StackTooltip({ active, payload, mode }: { active?: boolean; payload?: { payload: Record<string, number | string> }[]; mode: 'count' | 'share' }) {
  if (!active || !payload?.length) return null
  const row = payload[0].payload
  return (
    <div className="tooltip-dark">
      <strong>{String(row.full)}</strong>
      {BAND_ORDER.map((b) => (
        <div key={b} className="tooltip-dark__row"><span style={{ color: bandMeta(b).color }}><BandShapeIcon shape={bandMeta(b).shape} size={11} /></span>{bandMeta(b).label}<b className="mono">{row[b]}{mode === 'share' ? '%' : ''}</b></div>
      ))}
      <div className="tooltip-dark__row tooltip-dark__row--total">Equipos<b className="mono">{row.total}</b></div>
    </div>
  )
}

export function BandPills({ value, onChange, counts }: { value: string; onChange: (v: string) => void; counts?: Record<string, number> }) {
  const items = [{ value: 'all', label: 'Todos' }, ...BAND_ORDER.map((b) => ({ value: b, label: bandMeta(b).label }))]
  return (
    <div className="pills" role="group" aria-label="Filtrar por banda">
      {items.map((i) => (
        <button key={i.value} type="button" className={cx('pill', value === i.value && 'pill--active')} aria-pressed={value === i.value} onClick={() => onChange(i.value)}>
          {i.value !== 'all' ? <span style={{ color: bandMeta(i.value).color, display: 'inline-flex' }}><BandShapeIcon shape={bandMeta(i.value).shape} size={11} /></span> : null}
          {i.label}
          {counts && i.value !== 'all' ? <span className="pill__count mono">{counts[i.value] ?? 0}</span> : null}
        </button>
      ))}
    </div>
  )
}

/* ---------- average score trend ---------- */

export function TrendLine({ points, highlight }: { points: TrendPoint[]; highlight?: { from?: string; to?: string } }) {
  const data = points.map((p) => ({ ...p, avgPlot: p.avg }))
  const last = points.length ? points[points.length - 1].label : undefined
  const first = points.length ? points[0].label : undefined
  const from = highlight?.from ? points.find((p) => p.month >= highlight.from!.slice(0, 7))?.label : undefined
  const to = highlight?.to ? [...points].reverse().find((p) => p.month <= highlight.to!.slice(0, 7))?.label : undefined
  const areaFrom = from ?? (highlight?.to ? first : undefined)
  const areaTo = to ?? (highlight?.from ? last : undefined)
  return (
    <div className="chart" style={{ height: 280 }}>
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 520, height: 280 }}>
        <LineChart data={data} margin={{ top: 12, right: 12, bottom: 0, left: -12 }}>
          <CartesianGrid vertical={false} stroke="var(--line)" />
          {areaFrom && areaTo ? <ReferenceArea x1={areaFrom} x2={areaTo} fill="var(--brand-soft)" fillOpacity={0.7} ifOverflow="extendDomain" /> : null}
          <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={false} padding={{ left: 24, right: 24 }} />
          <YAxis domain={[0, (max: number) => Math.max(3, Math.ceil(max))]} tick={AXIS} tickLine={false} axisLine={false} />
          <ReferenceLine y={2.5} stroke="var(--band-critical)" strokeDasharray="5 4" label={{ value: 'Crítico desde 2.5', position: 'insideTopRight', fill: 'var(--band-critical-ink)', fontSize: 11 }} />
          <Tooltip content={<TrendTooltip />} cursor={{ stroke: 'var(--brand)', strokeDasharray: '4 4' }} />
          <Line type="monotone" dataKey="avgPlot" stroke="var(--brand)" strokeWidth={2.5} dot={{ r: 5, fill: '#fff', stroke: 'var(--brand)', strokeWidth: 2 }} activeDot={{ r: 7, fill: 'var(--brand)' }} connectNulls isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
      <table className="sr-only">
        <caption>Puntaje promedio por periodo</caption>
        <thead><tr><th>Periodo</th><th>Puntaje promedio</th><th>Inspecciones</th></tr></thead>
        <tbody>{points.map((p) => <tr key={p.month}><th>{p.label}</th><td>{p.avg ?? 'sin puntaje'}</td><td>{p.inspections}</td></tr>)}</tbody>
      </table>
    </div>
  )
}

function TrendTooltip({ active, payload }: { active?: boolean; payload?: { payload: TrendPoint }[] }) {
  if (!active || !payload?.length) return null
  const p = payload[0].payload
  return (
    <div className="tooltip-dark tooltip-dark--brand">
      <span className="tooltip-dark__muted">Periodo</span>
      <strong>{p.label}</strong>
      <span className="tooltip-dark__muted">Puntaje promedio</span>
      <strong className="mono">{p.avg === null ? '—' : p.avg.toFixed(2)}</strong>
      <span className="tooltip-dark__muted">Inspecciones</span>
      <strong className="mono">{p.inspections}</strong>
    </div>
  )
}

/* ---------- band share donut ---------- */

export function BandDonut({ model, onSelect, active }: { model: ReturnType<typeof bandDonut>; onSelect?: (band: string) => void; active?: string }) {
  return (
    <div className="donut">
      <div className="donut__chart" style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 200, height: 200 }}>
          <PieChart>
            <Pie data={model.slices} dataKey="count" nameKey="label" innerRadius="68%" outerRadius="100%" paddingAngle={2} cornerRadius={6} stroke="none" isAnimationActive={false}>
              {model.slices.map((s) => <Cell key={s.band} fill={bandFill(s.band)} stroke={s.band === 'not_assessed' ? 'var(--band-na)' : 'none'} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="donut__center"><span className="mono donut__total">{model.total}</span><span className="muted">Todos los equipos</span></div>
      </div>
      <ul className="legend">
        {model.legend.map((l) => (
          <li key={l.band}>
            <button type="button" className={cx('legend__row', active === l.band && 'is-active')} onClick={() => onSelect?.(l.band)} disabled={!onSelect}>
              <span className="legend__mark" style={{ color: l.color }}><BandShapeIcon shape={bandMeta(l.band).shape} size={12} /></span>
              <span className="legend__label">{l.label}</span>
              <span className="legend__count mono">{l.count}</span>
              <span className="legend__pct mono">{Math.round(l.pct)}%</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ---------- horizontal bar list ---------- */

export interface HBarItem {
  key: string | number
  label: string
  value: string
  widthPct: number
  tone?: string
  extra?: ReactNode
  onClick?: () => void
}

export function HBarList({ items, empty = 'Sin datos' }: { items: HBarItem[]; empty?: string }) {
  if (items.length === 0) return <p className="muted">{empty}</p>
  return (
    <ul className="hbars">
      {items.map((i) => (
        <li key={i.key} className="hbars__row">
          <span className="hbars__label" title={i.label}>{i.label}</span>
          <span className="hbars__track" aria-hidden="true">
            {i.widthPct > 0 ? <span className="hbars__fill" style={{ width: `${Math.max(i.widthPct, 4)}%`, background: i.tone }} /> : <span className="hbars__none" />}
          </span>
          <span className="hbars__value mono">{i.value}</span>
          {i.extra}
        </li>
      ))}
    </ul>
  )
}

/* ---------- sparkline: score history of one equipment ---------- */

/** A tiny line of the score history (decorative; the caller states the numbers in text). */
export function Sparkline({ points, width = 96, height = 28 }: { points: { date: string; total: number }[]; width?: number; height?: number }) {
  if (points.length < 2) return null
  return (
    <span className="sparkline" aria-hidden="true">
      <LineChart width={width} height={height} data={points} margin={{ top: 3, right: 3, bottom: 3, left: 3 }}>
        <YAxis hide domain={[0, 4]} />
        <ReferenceLine y={2.5} stroke="var(--band-critical)" strokeDasharray="3 3" />
        <Line dataKey="total" stroke="var(--brand)" strokeWidth={1.75} dot={{ r: 2 }} isAnimationActive={false} />
      </LineChart>
    </span>
  )
}
